import type { ISearchableDropdownOption } from '@/components/atoms/SearchableDropdown';
import { useCallback } from 'react';

import type { GetUIElementByType } from '@ballerine/common';

import type { TDynamicFormField } from '../../types';
import { SearchableDropdown } from '@/components/atoms/SearchableDropdown';
import { createTestId } from '@/components/organisms/Renderer';
import { useField } from '../../hooks/external';
import { useMountEvent } from '../../hooks/internal/useMountEvent';
import { useUnmountEvent } from '../../hooks/internal/useUnmountEvent';
import { FieldDescription } from '../../layouts/FieldDescription';
import { FieldErrors } from '../../layouts/FieldErrors';
import { FieldLayout } from '../../layouts/FieldLayout';
import { FieldPriorityReason } from '../../layouts/FieldPriorityReason';
import { useStack } from '../FieldList/providers/StackProvider';

export const SelectField: TDynamicFormField<GetUIElementByType<'selectfield'>> = ({ element }) => {
  useMountEvent(element);
  useUnmountEvent(element);

  const { stack } = useStack();
  const { value, disabled, onChange, onBlur, onFocus } = useField<string | undefined>(
    element,
    stack,
  );

  const {
    placeholder,
    options = [],
    optionNotFoundText = 'No options found',
  } = element.params || {};

  const handleChange = useCallback(
    (value: string) => {
      onChange(value);
    },
    [onChange],
  );

  return (
    <FieldLayout element={element}>
      <SearchableDropdown
        options={options as ISearchableDropdownOption[]}
        value={value}
        testId={createTestId(element, stack)}
        placeholder={placeholder}
        optionNotFoundText={optionNotFoundText}
        disabled={disabled}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      <FieldDescription element={element} />
      <FieldPriorityReason element={element} />
      <FieldErrors element={element} />
    </FieldLayout>
  );
};
